import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ModalCloseButton, ModalOverlay, ModalPanel } from "~/components/modal";
import { BIRTHDAY_MODAL, nextKstMidnightMs } from "~/data/oneroomta/birthday-modal";

interface BirthdayModalProps {
  onClose: () => void;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function BirthdayModal({ onClose }: BirthdayModalProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hideToday, setHideToday] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const handleClose = () => {
    audioRef.current?.pause();
    if (hideToday) {
      try {
        localStorage.setItem(BIRTHDAY_MODAL.storageKey, String(nextKstMidnightMs()));
      } catch {
        // localStorage 차단 환경에서는 저장 생략
      }
    }
    onClose();
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play();
    else audio.pause();
  };

  const handleSeek = (e: ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    if (!Number.isFinite(t)) return;
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  return (
    <ModalOverlay onClose={handleClose}>
      <ModalPanel className="max-w-[370px] overflow-hidden overflow-y-auto pt-4">
        <ModalCloseButton ariaLabel="닫기" />
        <div className="px-3">
          <img
            src={BIRTHDAY_MODAL.image}
            alt={BIRTHDAY_MODAL.imageAlt}
            className="block h-auto w-full"
          />
        </div>
        <div className="px-3 pt-4 pb-4 max-md:px-3">
          <div className="flex flex-col items-center rounded-2xl bg-[#f7f7f9] px-[15px] pt-[15px] pb-[12px]">
            <p className="mb-4 text-center text-[17px] font-medium leading-snug text-[var(--ip-text)] max-md:text-[16px]">
              {BIRTHDAY_MODAL.title}
            </p>
            <div className="mb-[-2px] flex w-full flex-col gap-1">
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.01}
                value={currentTime}
                onChange={handleSeek}
                disabled={duration === 0}
                aria-label="재생 위치"
                className="w-full accent-[#3a9ed4]"
              />
              <div className="flex justify-between text-xs tabular-nums text-[var(--ip-text-muted)]">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={togglePlay}
              aria-label={isPlaying ? "일시정지" : "재생"}
              style={{
                backgroundColor: "#3a9ed4",
                borderColor: "#1a1a1a",
                boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
              }}
              className="mt-[-5px] flex h-12 w-12 items-center justify-center rounded-full border text-white transition-transform hover:scale-105"
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5 translate-x-[2px]"
                  fill="currentColor"
                  aria-hidden
                >
                  <path d="M7 5v14l12-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 border-t border-[rgba(var(--ip-primary-glow),0.12)] px-6 py-3 text-sm text-[#a8a8a8] max-md:px-4">
          <input
            type="checkbox"
            checked={hideToday}
            onChange={(e) => setHideToday(e.target.checked)}
            className="h-4 w-4 accent-[#a8a8a8]"
          />
          <span>오늘 하루 보지 않기</span>
        </label>
        <audio ref={audioRef} src={BIRTHDAY_MODAL.audio} preload="metadata" />
      </ModalPanel>
    </ModalOverlay>
  );
}
